/* ============================================
   Tareas del CRM — todas juntas, de todas sus oportunidades

   Las tareas de una oportunidad vivían solo dentro de su ficha, así que para
   saber qué hay pendiente había que entrar una por una. Acá están las de
   todas: las del pipeline y las de la prospección, que son lo mismo en dos
   etapas.

   **Solo CRM, no proyectos.** Una tarea de proyecto tiene cliente, tarifa,
   horas y puede terminar facturada; una de oportunidad es trabajo comercial
   —llamar, mandar la propuesta, hacer seguimiento— y mezclarlas haría que la
   lista deje de responder «qué tengo que hacer para cerrar algo».

   Las mismas dos vistas que en la ficha (tabla y Kanban) y los mismos
   componentes: si mañana cambia la tabla de la ficha, cambia esta.
   ============================================ */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { Lead, Tarea } from '@/lib/types'
import { PageHeader, Badge } from '@/components/ui'
import { TareasKanban } from '@/components/crm/LeadTareasTab'
import { ESTADO_TAREA_META, estaVencida } from '@/lib/tareas-estado'
import { IconSearch, IconList, IconKanban } from '@/components/icons/Icons'

const CRUMBS = [{ label: 'CRM' }, { label: 'Tareas' }]
const TODAS = 'todas'

type Vista = 'tabla' | 'kanban'

export default function PartnerTareasCrm() {
  const api = useApi()
  const nav = useNavigate()

  const [tareas, setTareas] = useState<Tarea[]>([])
  const [leads, setLeads] = useState<Lead[]>([])
  const [cargando, setCargando] = useState(true)
  const [vista, setVista] = useState<Vista>('tabla')
  const [search, setSearch] = useState('')
  const [filtroLead, setFiltroLead] = useState(TODAS)
  const [filtroEstado, setFiltroEstado] = useState(TODAS)

  const cargar = useCallback(() => {
    setCargando(true)
    Promise.all([
      api.get<Tarea[]>('/api/tareas?solo_leads=1').catch(() => [] as Tarea[]),
      // Los prospectos van aparte: `GET /api/leads` los excluye salvo que se
      // pidan, y sus tareas también son del CRM.
      api.get<Lead[]>('/api/leads?incluir_prospectos=1').catch(() => [] as Lead[]),
    ]).then(([t, l]) => {
      setTareas(Array.isArray(t) ? t : [])
      setLeads(Array.isArray(l) ? l : [])
    }).finally(() => setCargando(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const leadPorId = useMemo(() => {
    const m = new Map<number, Lead>()
    for (const l of leads) m.set(l.id, l)
    return m
  }, [leads])

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase()
    return tareas.filter(t => {
      if (filtroLead !== TODAS && String(t.lead_id ?? '') !== filtroLead) return false
      if (filtroEstado !== TODAS && (t.estado || 'pendiente') !== filtroEstado) return false
      if (!q) return true
      const lead = t.lead_id ? leadPorId.get(t.lead_id) : null
      return [t.titulo, t.descripcion, lead?.empresa, lead?.titulo]
        .some(c => c?.toLowerCase().includes(q))
    })
  }, [tareas, search, filtroLead, filtroEstado, leadPorId])

  /** Vencidas primero y después por fecha: es el orden en que se trabajan. */
  const ordenadas = useMemo(() => {
    return [...filtradas].sort((a, b) => {
      const va = estaVencida(a) ? 0 : 1
      const vb = estaVencida(b) ? 0 : 1
      if (va !== vb) return va - vb
      return (a.fecha_limite || '9999').localeCompare(b.fecha_limite || '9999')
    })
  }, [filtradas])

  async function parchear(t: Tarea, cambios: Partial<Tarea>) {
    setTareas(prev => prev.map(x => (x.id === t.id ? { ...x, ...cambios } : x)))
    try {
      await api.put(`/api/tareas/${t.id}`, cambios)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo guardar', 'error')
      cargar()
    }
  }

  /* Abrir una tarea lleva a SU oportunidad: el detalle completo —adjuntos,
     dependencias, el lienzo con el contexto— vive ahí, y duplicarlo acá
     sería mantener dos fichas de lo mismo. */
  function abrir(t: Tarea) {
    if (t.lead_id) nav(`/partner/pipeline?lead=${t.lead_id}&tarea=${t.id}`)
  }

  const conteos = useMemo(() => {
    const vencidas = tareas.filter(estaVencida).length
    const abiertas = tareas.filter(t => !ESTADO_TAREA_META[(t.estado || 'pendiente') as keyof typeof ESTADO_TAREA_META]?.terminal).length
    return { vencidas, abiertas }
  }, [tareas])

  /* Los leads que efectivamente tienen tareas: un selector con los 40 leads
     de los que 3 tienen algo que hacer no ayuda a filtrar.

     Va ACÁ arriba y no junto al `return`: debajo del `if (cargando)` React
     cuenta un hook de más en el segundo render y la pantalla queda en blanco
     con «Rendered more hooks than during the previous render». */
  const leadsConTareas = useMemo(() => {
    const ids = new Set(tareas.map(t => t.lead_id).filter(Boolean) as number[])
    return [...ids].map(id => leadPorId.get(id)).filter(Boolean) as Lead[]
  }, [tareas, leadPorId])

  if (cargando) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div className="empty-state"><p>Cargando tareas...</p></div>
    </div>
  )

  const hayFiltros = search.trim() !== '' || filtroLead !== TODAS || filtroEstado !== TODAS
  const limpiar = () => { setSearch(''); setFiltroLead(TODAS); setFiltroEstado(TODAS) }

  return (
    <div className="page-enter page-shell">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={
          <>
            {conteos.abiertas} {conteos.abiertas === 1 ? 'abierta' : 'abiertas'}
            {conteos.vencidas > 0 && <> · <span className="tareas-crm__vencidas">{conteos.vencidas} vencida{conteos.vencidas === 1 ? '' : 's'}</span></>}
          </>
        }
        actions={
          <div className="tareas-vista">
            <button
              type="button"
              className={`btn btn-sm ${vista === 'tabla' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setVista('tabla')}
            >
              <IconList size={14} /> Tabla
            </button>
            <button
              type="button"
              className={`btn btn-sm ${vista === 'kanban' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setVista('kanban')}
            >
              <IconKanban size={14} /> Kanban
            </button>
          </div>
        }
      />

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input
            className="form-input"
            placeholder="Buscar por tarea, empresa u oportunidad..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="form-input" value={filtroLead} onChange={e => setFiltroLead(e.target.value)} style={{ width: 230 }}>
          <option value={TODAS}>Toda oportunidad</option>
          {leadsConTareas.map(l => (
            <option key={l.id} value={String(l.id)}>{l.empresa || l.titulo}</option>
          ))}
        </select>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 160 }}>
          <option value={TODAS}>Todo estado</option>
          {(Object.keys(ESTADO_TAREA_META) as (keyof typeof ESTADO_TAREA_META)[]).map(e => (
            <option key={e} value={e}>{ESTADO_TAREA_META[e].label}</option>
          ))}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiar}>Limpiar filtros</button>
        )}
      </div>

      {tareas.length === 0 ? (
        <div className="empty-state">
          <h3>Sin tareas</h3>
          <p>
            Acá se juntan las tareas de todas tus oportunidades, las del pipeline y las de
            prospección. Se crean desde la ficha de cada una, en su pestaña Tareas.
          </p>
        </div>
      ) : ordenadas.length === 0 ? (
        <div className="empty-state">
          <h3>Ninguna tarea coincide</h3>
          <p>Prueba con otros filtros.</p>
          <button className="btn btn-secondary" onClick={limpiar}>Limpiar filtros</button>
        </div>
      ) : vista === 'tabla' ? (
        <div className="tareas-crm__tabla">
          {/* De qué oportunidad es cada una: en la ficha sobra, acá es lo
              primero que hay que saber para decidir si atenderla. */}
          <ul className="tareas-crm__grupos">
            {ordenadas.map(t => {
              const lead = t.lead_id ? leadPorId.get(t.lead_id) : null
              return (
                <li key={t.id} className="tareas-crm__fila" onClick={() => abrir(t)} role="button" tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') abrir(t) }}>
                  <div className="tareas-crm__principal">
                    <span className="tareas-crm__titulo">{t.titulo}</span>
                    {lead && (
                      <span className="tareas-crm__lead">
                        {lead.empresa || lead.titulo}
                        {lead.etapa === 'prospecto' && <span className="tareas-crm__chip">prospecto</span>}
                      </span>
                    )}
                  </div>
                  <span className={`tareas-crm__fecha${estaVencida(t) ? ' is-vencida' : ''}`}>
                    {t.fecha_limite ? new Date(`${t.fecha_limite.slice(0, 10)}T12:00:00`).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) : '—'}
                  </span>
                  <Badge variant={ESTADO_TAREA_META[(t.estado || 'pendiente') as keyof typeof ESTADO_TAREA_META]?.variant ?? 'neutral'}>
                    {ESTADO_TAREA_META[(t.estado || 'pendiente') as keyof typeof ESTADO_TAREA_META]?.label ?? t.estado}
                  </Badge>
                </li>
              )
            })}
          </ul>
        </div>
      ) : (
        <TareasKanban tareas={ordenadas} onAbrir={abrir} onParchear={parchear} />
      )}
    </div>
  )
}
